import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useEffect, useId, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import chevronUpSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-up-small';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { InvisibleButton, OrangeButton, PrimeButton } from 'src/common/buttons';
import { ExternalLink } from 'src/common/externalLink';
import { PrimeBadge } from 'src/common/icons';
import { useAnalyticsContext } from 'src/common/metrics';
import { BodyText, ErrorBar, ImageBlock, QuantityBadge, SectionDivider, SmallText } from 'src/common/styled';
import { useOffRampToAmazon, usePageContext, usePreviewOrder, usePreviewOrderAlert } from 'src/hooks';
import { useConfirmOrderMutation } from 'src/hooks/useConfirmOrders';
import { SavingsChargeGroup } from 'src/pages/previewOrderPage/components/SavingsChargeGroup';
import { TaxBreakdownModal } from 'src/pages/previewOrderPage/components/TaxBreakdownModal';
import { createPreviewOrderRequest, getChargeRows, getPreviewOrderState, PreviewOrderErrorType, PreviewOrderStatus, } from 'src/pages/previewOrderPage/utils';
import { useBagStore, useCreatorAttributionStore, usePaymentStore } from 'src/store';
import { formatWeekdayDate } from 'src/utils/date';
import { RetailRoutes, useRetailRedirectUrls } from 'src/utils/links';
import { isEnvironmentSecureForMFA } from 'src/utils/MFAEnvironmentIntegrity';
import { withOneClickReopen } from 'src/utils/paymentSelectionUtils';
import { getFirstOrderItem, getOrderAction, getOrderDetails } from '../../utils';
// Figma's product image; Rio has no 66px size
const PRODUCT_IMAGE_SIZE = '66px';
// Only the body scrolls, so the header and button stay in view
export const ScrollBody = styled.div `
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;
// Shared with the payment picker's header
export const headerPadding = ({ theme }) => css `
  padding: ${theme.rio.base.size[20]} ${theme.rio.base.size[20]} ${theme.rio.semantic.size.space.large};
`;
const Header = styled(Row) `
  ${headerPadding}
`;
export const BodyColumn = styled(Column) `
  padding: ${({ theme }) => `0 ${theme.rio.base.size[20]}`};
`;
export const Footer = styled(Column) `
  padding: ${({ theme }) => `${theme.rio.semantic.size.space.large} ${theme.rio.base.size[20]} ${theme.rio.semantic.size.space.extraLarge}`};
`;
const ProductImage = styled(ImageBlock) `
  flex-shrink: 0;
  width: ${PRODUCT_IMAGE_SIZE};
  height: ${PRODUCT_IMAGE_SIZE};
  justify-content: center;

  img {
    max-width: 100%;
  }
`;
export const DateHeading = styled(Text) `
  && {
    font-size: ${({ theme }) => theme.rio.semantic.size.font.medium};
    line-height: ${({ theme }) => theme.rio.semantic.size.lineHeight.medium};
    color: ${({ theme }) => theme.rio.semantic.color.font.primary};
  }
`;
// Lines the 12px icon, not the 32px button, up with Figma's position
const CloseSlot = styled.div `
  position: relative;
  top: ${({ theme }) => `calc(${theme.rio.semantic.size.space.small} - ${theme.rio.base.size[10]})`};
  left: ${({ theme }) => theme.rio.semantic.size.space.medium};
`;
// Figma's 60px label column plus a 22px gap; padding because Meridian Row owns child margins
const RowLabel = styled(BodyText) `
  flex-shrink: 0;
  box-sizing: border-box;
  min-width: ${({ theme }) => `calc(${theme.rio.base.size[40]} + ${theme.rio.base.size[20]} + ${theme.rio.base.size[14]})`};
  padding-inline-end: ${({ theme }) => theme.rio.base.size[14]};
`;
// POP's gray bar for a value the preview doesn't have
export const Placeholder = styled.span `
  display: inline-block;
  width: ${({ theme }) => theme.rio.base.size[100]};
  max-width: 100%;
  height: ${({ theme }) => theme.rio.base.size[14]};
  vertical-align: middle;
  border-radius: ${({ theme }) => theme.rio.semantic.size.radius.small};
  background-color: ${({ theme }) => theme.rio.base.color.gray[theme.mode === 'dark' ? 600 : 1300]};
`;
// Stretched into the body's side padding so the row's label stays aligned
const RowErrorBar = styled(ErrorBar) `
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ theme }) => `calc(-1 * ${theme.rio.semantic.size.space.medium})`};
  height: auto;
`;
// Fills the sheet's remaining height as a column
export const FillColumn = styled.div `
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;
export const LoadingFrame = styled(FillColumn) `
  position: relative;
`;
// Hides the content while it loads, without changing the sheet's size
export const LoadingCover = styled.div `
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.rio.semantic.color.background.primary};
`;
const SingleLineValue = styled(BodyText) `
  flex: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  word-break: break-all;
  overflow: hidden;
`;
// Clips Meridian's chevron, which overflows its box by a sub-pixel and would make the body scroll
const Chevron = styled.span `
  display: flex;
  overflow: hidden;
`;
const TaxLink = styled(InvisibleButton) `
  font: inherit;
  color: ${({ theme }) => theme.rio.semantic.color.link.default};
`;
// Anchors the row's error bar
const RowFrame = styled(Row) `
  position: relative;
`;
const RowButton = styled(InvisibleButton) `
  display: block;
  width: 100%;
  padding: 0;
  text-align: start;
  color: inherit;

  &:disabled,
  &[aria-disabled='true'] {
    cursor: default;
  }
`;
const DetailRow = ({ label, hasError = false, onClick, disabled, testId, children }) => {
    const row = (_jsxs(RowFrame, { spacing: "300", alignmentVertical: "stretch", width: "100%", "data-testid": onClick ? undefined : testId, children: [hasError && _jsx(RowErrorBar, { "data-testid": "one-click-row-error" }), _jsx(RowLabel, { rioColor: "link", children: label }), _jsx(SingleLineValue, { children: children !== null && children !== void 0 ? children : _jsx(Placeholder, {}) }), onClick && !disabled && (_jsx(Chevron, { children: _jsx(Icon, { tokens: chevronRightSmallTokens, color: "primary" }) }))] }));
    if (!onClick)
        return row;
    return (_jsx(RowButton, { type: "button", onClick: disabled ? undefined : onClick, "aria-disabled": disabled, "data-testid": testId, children: row }));
};
/** The order view of the 1-Click sheet (Figma 2.2): POP's preview, confirmed with the shared mutation. */
export const OrderSheetView = ({ onDismiss, onOrderConfirmed, onError, onPlaceOrder, onChangePayment, isBusy, orderError, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { bundle, checkoutBundles } = usePageContext();
    const { previewOrderPage: popBundle, dogPage: dogPageBundle } = checkoutBundles;
    const locale = useLocalizationContext().localizationContext.getLocale();
    const rumClient = useAnalyticsContext();
    const offRampToAmazon = useOffRampToAmazon();
    const [, setSearchParams] = useSearchParams();
    const location = useLocation();
    const bagContents = useBagStore().listBag();
    const bagItem = bagContents[0];
    const { selectedPaymentMethodId } = usePaymentStore();
    const { getCreatorAttributionToken } = useCreatorAttributionStore();
    const redirectUrls = useRetailRedirectUrls([
        RetailRoutes.CONDITIONS_OF_USE,
        RetailRoutes.PAYMENT_SELECTION_WALLET,
        RetailRoutes.PRIVACY_NOTICE,
        RetailRoutes.ADDRESS_LIST,
        RetailRoutes.DETAIL_PAGE,
        RetailRoutes.ORDER_HISTORY,
    ], bagItem.asin);
    // Held while an order is in flight or failed, so a window-focus refetch can't change what is being ordered
    const preview = usePreviewOrder(createPreviewOrderRequest(bagContents, selectedPaymentMethodId, getCreatorAttributionToken()), { enabled: !isBusy && !orderError, keepPrevious: true });
    const { orderStatus, errorType, translationKey } = getPreviewOrderState((_b = (_a = preview.data) === null || _a === void 0 ? void 0 : _a.errorReason) === null || _b === void 0 ? void 0 : _b.code, preview.isError);
    const isFetching = preview.isLoading || preview.isRefetching;
    const isButtonBusy = isBusy || isFetching;
    const previewOrderAlert = usePreviewOrderAlert({
        orderPreview: preview.data,
        orderStatus,
        errorType,
        translationKey,
        bundle: popBundle,
        redirectUrls,
        selectedPaymentMethodId,
        source: ReportActionSource.OneClickCheckoutSheet,
    });
    const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    // The first load, a new card's preview or Retry; a window-focus refetch only busies the button
    const isCovered = (preview.isLoading || preview.isPlaceholderData || isRetrying) && !isBusy;
    // The rows lock while covered or while an order is in flight or failed, not on a window-focus refetch
    const isLocked = isCovered || isBusy || !!orderError;
    const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
    const breakdownId = useId();
    // Guards against a second confirm from rapid taps before the button disables
    const isSubmittingRef = useRef(false);
    const { mutate } = useConfirmOrderMutation({
        onOrderConfirmed,
        onPaymentError: () => onError('payment'),
        onGenericError: () => onError('generic'),
        onSettled: () => {
            isSubmittingRef.current = false;
        },
    });
    // One result per resolved preview, including refetches
    useEffect(() => {
        if (isFetching)
            return;
        rumClient.recordEvent('ONE_CLICK_PREVIEW_RESULT', {
            status: PreviewOrderStatus[orderStatus],
            errorType: PreviewOrderErrorType[errorType],
        });
    }, [isFetching, preview.dataUpdatedAt, preview.errorUpdatedAt, orderStatus, errorType, rumClient]);
    const closeButton = (_jsx(CloseSlot, { children: _jsx(Button, { type: "icon", size: "small", "aria-label": bundle.getMessage('close'), onClick: () => onDismiss('close'), "data-testid": "one-click-close", children: _jsx(Icon, { tokens: closeSmallTokens }) }) }));
    const orderItem = getFirstOrderItem(preview.data);
    const isUnfixable = orderStatus === PreviewOrderStatus.UNFIXABLE_ERROR;
    // As on POP, an order that can't be placed shows no cost breakdown or tax summary
    const chargeRows = ((_c = preview.data) === null || _c === void 0 ? void 0 : _c.charges) && !isUnfixable ? getChargeRows(preview.data.charges, locale, popBundle) : undefined;
    const { items: taxItems, message: taxMessage } = (_e = (_d = preview.data) === null || _d === void 0 ? void 0 : _d.taxSummary) !== null && _e !== void 0 ? _e : {};
    const taxFragments = !isUnfixable && (taxItems === null || taxItems === void 0 ? void 0 : taxItems.length) && ((_f = taxMessage === null || taxMessage === void 0 ? void 0 : taxMessage.fragments) === null || _f === void 0 ? void 0 : _f.length) ? taxMessage.fragments : undefined;
    const details = getOrderDetails(preview.data, errorType, bundle, popBundle, preview.isLoading);
    const orderAction = getOrderAction(orderStatus);
    const OrderButton = bagItem.isPrime ? PrimeButton : OrangeButton;
    const link = (chunk, href, id, underline = false) => (_jsx(ExternalLink, { id: id, href: href, underline: underline, source: ReportActionSource.OneClickCheckoutSheet, children: chunk }, id));
    // A failed order replaces any preview alert with DogPage's message, the one POP and TYP show
    const alert = orderError
        ? {
            type: 'error',
            title: dogPageBundle.getMessage(orderError === 'payment' ? 'paymentHasNotBeenVerified' : 'somethingWentWrong'),
            message: orderError === 'payment'
                ? dogPageBundle.formatMessage('bankConnectionFailTryAgainOnAmazon', {
                    a: (chunk) => link(chunk, redirectUrls.ORDER_HISTORY, 'order-history-link', true),
                })
                : dogPageBundle.formatMessage('tryAgainLaterOrViewOnAmazon', {
                    a: (chunk) => link(chunk, redirectUrls.DETAIL_PAGE, 'detail-page-link', true),
                }),
        }
        : previewOrderAlert;
    const onOrderButtonClick = () => {
        var _a;
        if (orderAction.action === 'backToProduct')
            return onDismiss('backToProduct');
        if (orderAction.action === 'retry') {
            setIsRetrying(true);
            return void preview.refetch().finally(() => setIsRetrying(false));
        }
        if (isSubmittingRef.current || !((_a = preview.data) === null || _a === void 0 ? void 0 : _a.purchaseId))
            return;
        isSubmittingRef.current = true;
        rumClient.recordEvent('PLACE_ORDER_CLICK', { oneClickArm: 'T1', surface: 'ONE_CLICK_SHEET' });
        onPlaceOrder();
        mutate({ purchaseId: preview.data.purchaseId, isEnvironmentSecureForMFA: isEnvironmentSecureForMFA(rumClient) });
    };
    // Flags the PDP so a reload on return re-opens the sheet
    const onChangeAddress = () => {
        rumClient.recordEvent('ONE_CLICK_CHANGE_ADDRESS_CLICK', {});
        setSearchParams((prev) => withOneClickReopen(prev, true), { replace: true, state: location.state });
        offRampToAmazon(redirectUrls.ADDRESS_LIST, ReportActionSource.OneClickCheckoutSheet);
    };
    const onTotalClick = () => {
        rumClient.recordEvent('ONE_CLICK_BREAKDOWN_TOGGLE', { expanded: !isBreakdownOpen });
        setIsBreakdownOpen(!isBreakdownOpen);
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Header, { spacing: "400", widths: ['fit', 'fill', 'fit'], alignmentVertical: "top", children: [_jsxs(ProductImage, { spacing: "none", children: [_jsx("img", { src: bagItem.productImageUrl, alt: (_g = orderItem === null || orderItem === void 0 ? void 0 : orderItem.title) !== null && _g !== void 0 ? _g : '', "data-testid": "one-click-product-image" }), _jsx(QuantityBadge, { alignmentHorizontal: "center", width: "24px", height: "24px", children: _jsx(Text, { type: "b100", "data-testid": "one-click-product-quantity", children: (_h = orderItem === null || orderItem === void 0 ? void 0 : orderItem.quantity) !== null && _h !== void 0 ? _h : bagItem.quantity }) })] }), _jsxs(Column, { spacing: "200", children: [(orderItem === null || orderItem === void 0 ? void 0 : orderItem.estimatedArrival) && (_jsx(DateHeading, { "data-font": "display", "data-testid": "one-click-delivery-date", children: formatWeekdayDate(orderItem.estimatedArrival, locale) })), bagItem.isPrime && _jsx(PrimeBadge, {})] }), closeButton] }), _jsx(Fragment, { children: _jsxs(LoadingFrame, { children: [_jsx(ScrollBody, { children: _jsxs(BodyColumn, { spacing: "350", children: [alert && (_jsx(Alert, { type: alert.type, "data-testid": orderError ? 'one-click-order-error' : 'one-click-alert', children: _jsxs(Column, { spacing: "none", children: [alert.title && _jsx("b", { children: alert.title }), _jsx("div", { children: alert.message })] }) })), _jsx(SectionDivider, { subtle: true }), _jsx(DetailRow, { label: details.shipToLabel, hasError: errorType === PreviewOrderErrorType.DELIVERY_ADDRESS, onClick: onChangeAddress, disabled: isLocked, testId: "one-click-change-address", children: details.shipTo && _jsx("span", { "data-testid": "one-click-ship-to", children: details.shipTo }) }), _jsx(SectionDivider, { subtle: true }), _jsx(DetailRow, { label: details.payWithLabel, hasError: errorType === PreviewOrderErrorType.PAYMENT_METHOD, onClick: onChangePayment, disabled: isLocked, testId: "one-click-change-payment", children: details.payWith && _jsx("span", { "data-testid": "one-click-pay-with", children: details.payWith }) }), preview.isLoading && (_jsxs(_Fragment, { children: [_jsx(SectionDivider, { subtle: true }), _jsx(DetailRow, { label: details.totalLabel, testId: "one-click-total-loading" })] })), chargeRows && (_jsxs(_Fragment, { children: [_jsx(SectionDivider, { subtle: true }), _jsxs("div", { children: [_jsx(RowButton, { type: "button", "aria-expanded": isBreakdownOpen, "aria-controls": breakdownId, onClick: onTotalClick, "data-testid": "one-click-total", children: _jsxs(Row, { spacing: "300", alignmentVertical: "center", widths: ['fit', 'fill', 'fit'], children: [_jsx(RowLabel, { rioColor: "link", children: details.totalLabel }), _jsx(BodyText, { "data-testid": "one-click-total-amount", children: _jsx("b", { children: chargeRows.formattedTotal }) }), _jsx(Chevron, { children: _jsx(Icon, { tokens: isBreakdownOpen ? chevronUpSmallTokens : chevronDownSmallTokens, color: "primary" }) })] }) }), _jsx("div", { id: breakdownId, children: isBreakdownOpen && (_jsxs(Column, { spacing: "300", spacingInset: "350 none none none", "data-testid": "one-click-breakdown", children: [((_k = (_j = preview.data) === null || _j === void 0 ? void 0 : _j.charges) === null || _k === void 0 ? void 0 : _k.discountApplied) && (_jsx(SmallText, { rioColor: "success", children: popBundle.getMessage('bestDeal') })), chargeRows.rows.map((row) => {
                                                                    var _a;
                                                                    return ((_a = row.charge.nestedCharges) === null || _a === void 0 ? void 0 : _a.length) ? (_jsx(SavingsChargeGroup, { charge: row.charge, label: row.label, locale: locale }, row.key)) : (_jsxs(Row, { width: "100%", alignmentHorizontal: "justify", "data-testid": "one-click-charge", children: [_jsx(SmallText, { children: row.label }), _jsx(SmallText, { rioColor: row.isSavings ? 'success' : undefined, children: row.formattedAmount })] }, row.key));
                                                                })] })) })] })] })), taxFragments && (_jsx(Text, { type: "b100", "data-testid": "one-click-tax-summary", children: taxFragments.map((fragment, index) => {
                                            var _a, _b;
                                            return (_jsxs("span", { children: [fragment.text, ((_b = (_a = fragment.link) === null || _a === void 0 ? void 0 : _a.content) === null || _b === void 0 ? void 0 : _b.text) && (_jsx(TaxLink, { type: "button", onClick: () => setIsTaxModalOpen(true), "data-testid": "one-click-tax-link", children: fragment.link.content.text }))] }, index));
                                        }) }))] }) }), _jsxs(Footer, { spacing: "350", children: [_jsx(OrderButton, { disabled: isButtonBusy || !!orderError, onClick: onOrderButtonClick, "data-testid": "one-click-order-button", children: isButtonBusy ? _jsx(Loader, { size: "small" }) : popBundle.getMessage(orderAction.key) }), (preview.isLoading || orderStatus === PreviewOrderStatus.SUCCESS) && (_jsx(SmallText, { "data-testid": "one-click-legal-note", children: popBundle.formatMessage('legalNote', {
                                        conditionsLink: (chunk) => link(chunk, redirectUrls.CONDITIONS_OF_USE, 'conditions-link'),
                                        privacyLink: (chunk) => link(chunk, redirectUrls.PRIVACY_NOTICE, 'privacy-link'),
                                    }) }))] }), isCovered && (_jsx(LoadingCover, { "data-testid": "one-click-order-loading", children: _jsx(Loader, { size: "small" }) }))] }) }, orderError !== null && orderError !== void 0 ? orderError : 'order'), ((_l = preview.data) === null || _l === void 0 ? void 0 : _l.taxSummary) && (_jsx(TaxBreakdownModal, { isOpen: isTaxModalOpen, onClose: () => setIsTaxModalOpen(false), taxSummary: preview.data.taxSummary }))] }));
};
